// src/components/Footer.js
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchBusinessById } from "./slices/businessSlice";
import "./styles/Footer.css";

const BUSINESS_ID = "6709f0331882028713f23319";

const capitalize = s => s.charAt(0).toUpperCase() + s.slice(1);

const formatTime = t => {
  if (!t) return "";
  // ISO timestamp?
  if (t.includes("T")) {
    const d = new Date(t);
    return d.toLocaleTimeString("en-US", {
      hour:   "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
  // fallback: assume "HH:mm"
  const [hStr, mStr] = t.split(":");
  const h = parseInt(hStr, 10), m = parseInt(mStr, 10);
  if (isNaN(h) || isNaN(m)) return t;
  const ampm = h >= 12 ? "PM" : "AM";
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}:${m.toString().padStart(2, "0")} ${ampm}`;
};

const Footer = () => {
  const dispatch = useDispatch();
  const { business, loading, error } = useSelector(s => s.business);

  useEffect(() => {
    dispatch(fetchBusinessById(BUSINESS_ID));
  }, [dispatch]);

  const hours = business?.hours || {};

  return (
    <footer className="app-footer">
      <div className="footer-content">
        {/* Contact Us */}
        <div className="footer-contact">
          <h4>Contact Us</h4>
          <p>
            {business?.address?.street}<br/>
            {business?.address?.city}, {business?.address?.state} {business?.address?.postalCode}
          </p>
          <p>Phone: {business?.phone}</p>
          {business?.website && (
            <p>
              <a href={business.website} target="_blank" rel="noopener noreferrer">
                {business.website}
              </a>
            </p>
          )}
        </div>

        {/* Map */}
        <div className="footer-map">
          <h4>Find Us</h4>
          <iframe
            title="Map location"
            src={
              business?.address
                ? `https://www.google.com/maps/embed/v1/place?key=AIzaSyB9AoMG7SbKCWQHsVUX4U1uxWmAMIoNaL8&q=${encodeURIComponent(
                    `${business.address.street}, ${business.address.city}, ${business.address.state}`
                  )}`
                : ""
            }
            width="100%"
            height="150"
            style={{ border: "none" }}
            allowFullScreen
            loading="lazy"
          />
        </div>

        {/* Store Hours */}
        <div className="footer-hours">
          <h4>Store Hours</h4>
          {loading && <p>Loading hours…</p>}
          {error && <p style={{ color: "red" }}>Error: {error}</p>}
          {!loading && !error && (
            <ul>
              {Object.entries(hours).map(([day, { open, close, closed }]) => (
                <li key={day}>
                  {capitalize(day)}:{" "}
                  {closed
                    ? "Closed"
                    : open && close
                      ? `${formatTime(open)} – ${formatTime(close)}`
                      : "Closed"
                  }
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Social Icons */}
        <div className="footer-social">
          <h4>Follow Us</h4>
          <div className="footer-icons">
            {business?.socialMedia?.instagram && (
              <a
                href={business.socialMedia.instagram}
                className="footer-icon"
                aria-label="Instagram"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-instagram"></i>
              </a>
            )}
            {business?.socialMedia?.facebook && (
              <a
                href={business.socialMedia.facebook}
                className="footer-icon"
                aria-label="Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-facebook-f"></i>
              </a>
            )}
            {business?.socialMedia?.tiktok && (
              <a
                href={business.socialMedia.tiktok}
                className="footer-icon"
                aria-label="TikTok"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-tiktok"></i>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="footer-bottom">
        <p>
          &copy; {new Date().getFullYear()} {business?.name || "Your Business"}. All rights reserved.
        </p>
        <p>Designed by AWE LLC</p>
      </div>
    </footer>
  );
};

export default Footer;
